'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { Button } from '../../../components/actions/button/index.js';
import { Spinner } from '../../../components/feedback/spinner/index.js';
import { VisuallyHidden } from '../../../components/layout/visually-hidden/index.js';
import { cx } from '../../../internal/cx.js';
import { allowValue, documentBase, exactOrigins, originOf, sandboxValue, } from '../../../internal/shell/embed.js';
import { focusBeside } from '../../../internal/shell/focus.js';
import { ExternalGlyph } from '../../../internal/shell/icons.js';
import { useVisibleTimeout } from '../../../internal/shell/use-visible-timeout.js';
import { APP_EMBED_PROTOCOL, decodeAppEmbedChildMessage, encodeAppEmbedMessage, } from './embedded-app-frame.protocol.js';
const DEFAULT_MESSAGES = {
    loading: 'Loading…',
    errorTitle: 'This page didn’t open',
    errorDescription: 'The app didn’t respond in time. It may be briefly unavailable. Try again, or open the page directly.',
    retry: 'Try again',
    open: 'Open in a new tab',
    newTab: 'opens in a new tab',
};
/**
 * The default sandbox: the embedded Rook page may run scripts, keep its own
 * same-origin session (cookies) and submit forms. It may not open popups,
 * download files, show modal dialogs or navigate the host page.
 */
export const DEFAULT_EMBED_SANDBOX = [
    'allow-scripts',
    'allow-same-origin',
    'allow-forms',
];
function cssLength(v) {
    return typeof v === 'number' ? `${v}px` : v;
}
/**
 * Embeds a page of another Rook app (App Settings embed contract v1): an
 * iframe with a minimal sandbox, a ready handshake, auto-height and focus
 * handoff over postMessage, and loading and error states with Try again. Only
 * messages from the frame's own window at an allowed origin are read, and
 * nothing is ever posted to `*`.
 */
export const EmbeddedAppFrame = forwardRef(function EmbeddedAppFrame({ src, title, allowedOrigins, protocol = APP_EMBED_PROTOCOL, sandbox = DEFAULT_EMBED_SANDBOX, allow = [], referrerPolicy = 'strict-origin-when-cross-origin', autoHeight = true, height = 480, minHeight = 120, maxHeight, readyTimeout = 12_000, openHref, focusOnReady = false, onReady, onStatusChange, onFocusExit, iframeRef, loadingContent, messages: messagesProp, className, style, ...rest }, ref) {
    const messages = useMemo(() => ({ ...DEFAULT_MESSAGES, ...messagesProp }), [messagesProp]);
    const frame = useRef(null);
    const root = useRef(null);
    const overlay = useRef(null);
    useImperativeHandle(ref, () => root.current);
    useImperativeHandle(iframeRef, () => frame.current, []);
    const [status, setStatusState] = useState('loading');
    const [attempt, setAttempt] = useState(0);
    const [reported, setReported] = useState(null);
    const [retried, setRetried] = useState(false);
    // Resolved after mount so server and client render the same markup.
    const [base, setBase] = useState(undefined);
    useEffect(() => setBase(documentBase()), []);
    const srcOrigin = originOf(src, base);
    const allowed = useMemo(() => exactOrigins(allowedOrigins ?? (srcOrigin ? [srcOrigin] : [])), 
    // A new array with the same entries must not reset the listener.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [srcOrigin, (allowedOrigins ?? []).join('\n')]);
    const latest = useRef({ onReady, onStatusChange, onFocusExit, focusOnReady });
    useEffect(() => {
        latest.current = { onReady, onStatusChange, onFocusExit, focusOnReady };
    });
    const statusRef = useRef('loading');
    const setStatus = useCallback((s) => {
        if (statusRef.current === s)
            return;
        statusRef.current = s;
        setStatusState(s);
        latest.current.onStatusChange?.(s);
    }, []);
    const post = useCallback((message) => {
        const win = frame.current?.contentWindow;
        // Only ever to the page's own origin, and only when that origin is allowed.
        if (!win || !srcOrigin || !allowed.has(srcOrigin))
            return;
        try {
            win.postMessage(encodeAppEmbedMessage(message, protocol), srcOrigin);
        }
        catch {
            /* the frame navigated away: nothing to tell */
        }
    }, [allowed, srcOrigin, protocol]);
    useEffect(() => {
        const onMessage = (e) => {
            const win = frame.current?.contentWindow;
            if (!win || e.source !== win || !allowed.has(e.origin))
                return;
            const m = decodeAppEmbedChildMessage(e.data, protocol);
            if (!m)
                return;
            if (m.type === 'ready') {
                if (statusRef.current !== 'ready') {
                    setStatus('ready');
                    latest.current.onReady?.();
                    if (latest.current.focusOnReady) {
                        frame.current?.focus();
                        post({ type: 'focus', direction: 'forward' });
                    }
                }
            }
            else if (m.type === 'resize') {
                setReported(m.height);
            }
            else if (m.type === 'focus-exit') {
                if (latest.current.onFocusExit)
                    latest.current.onFocusExit(m.direction);
                else if (frame.current && !focusBeside(frame.current, m.direction))
                    root.current?.focus();
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [allowed, protocol, post, setStatus]);
    // Each (re)load starts over.
    useEffect(() => {
        setStatus('loading');
    }, [src, attempt, setStatus]);
    useVisibleTimeout(status === 'loading', readyTimeout, () => {
        if (statusRef.current === 'loading')
            setStatus('error');
    }, `${src}\u0000${attempt}`);
    // After Try again, keep focus in the frame's area instead of losing it to the page.
    useEffect(() => {
        if (retried && status === 'loading')
            overlay.current?.focus();
    }, [retried, status, attempt]);
    const retry = () => {
        setReported(null);
        setRetried(true);
        setAttempt((n) => n + 1);
    };
    const fixed = !autoHeight || reported === null;
    const autoPx = reported === null
        ? null
        : Math.max(minHeight, maxHeight !== undefined ? Math.min(reported, maxHeight) : reported);
    const frameHeight = fixed ? cssLength(height) : `${autoPx}px`;
    return (_jsxs("div", { ref: root, tabIndex: -1, ...rest, className: cx('mn-embedded-app-frame', className), "data-status": status, "data-auto-height": autoHeight || undefined, style: { ...style, ['--mn-embedded-app-frame-height']: frameHeight }, children: [_jsx("iframe", { ref: frame, className: "mn-embedded-app-frame__frame", src: src, title: title, sandbox: sandboxValue(sandbox), allow: allowValue(allow), referrerPolicy: referrerPolicy, 
                // A page that announced `ready` before this listener existed says it again.
                onLoad: () => post({ type: 'ping' }), "aria-hidden": status === 'error' || undefined, tabIndex: status === 'error' ? -1 : undefined }, attempt), status === 'loading' ? (_jsx("div", { ref: overlay, className: "mn-embedded-app-frame__overlay", role: "status", tabIndex: -1, children: loadingContent ?? (_jsxs(_Fragment, { children: [_jsx(Spinner, { size: "md" }), _jsx("span", { children: messages.loading })] })) })) : null, status === 'error' ? (_jsxs("div", { className: "mn-embedded-app-frame__overlay", "data-error": "", children: [_jsxs("div", { className: "mn-embedded-app-frame__error", role: "alert", children: [_jsx("p", { className: "mn-embedded-app-frame__error-title", children: messages.errorTitle }), _jsx("p", { className: "mn-embedded-app-frame__error-text", children: messages.errorDescription })] }), _jsxs("div", { className: "mn-embedded-app-frame__actions", children: [_jsx(Button, { variant: "secondary", size: "sm", onClick: retry, children: messages.retry }), openHref ? (_jsx(Button, { asChild: true, variant: "ghost", size: "sm", trailingIcon: _jsx(ExternalGlyph, {}), children: _jsxs("a", { href: openHref, target: "_blank", rel: "noopener noreferrer", children: [messages.open, _jsxs(VisuallyHidden, { children: [" (", messages.newTab, ")"] })] }) })) : null] })] })) : null] }));
});
//# sourceMappingURL=embedded-app-frame.js.map